// A ticket's screenshots (packages/shared domain/ticket.ts): picking them for a new ticket (paste a
// screenshot with Ctrl+V, drop pictures on the box, or choose them), and showing a ticket's as thumbnails
// that open full size (the chat's photo viewer). Its sender adds more or takes one off while it's open.
// A big picture is scaled down to a JPEG in the browser first (prepareChatFile, as chat photos are), and
// only pictures the browser can show go on (a HEIC this browser can't read is turned away, with why).
import { useCallback, useEffect, useRef, useState, type DragEvent } from 'react';
import { TICKET_MAX_FILES, TICKET_MAX_FILE_BYTES, chatFileKind, formatFileSize, ticketFileUrl, type Ticket } from '@technohull/shared';
import { prepareChatFile, type PreparedFile } from '../collab/chatUploads.js';
import { ChatLightbox } from '../collab/ChatLightbox.js';
import { imageTypeOf } from '../lib/shrinkImage.js';
import { useNameResolver } from '../features/directory.js';
import { uploadTicketFile, useRefreshTickets, useRemoveTicketFile } from '../features/tickets.js';
import { InlineError } from './ui.js';
import { IconClose } from './icons.js';

const isPicture = (f: File) => {
  const t = imageTypeOf(f);
  return t.startsWith('image/') && !t.includes('svg');
};

/** a picture ready to send, or why it can't go */
async function prepareShot(file: File): Promise<PreparedFile> {
  const out = await prepareChatFile(file);
  if (chatFileKind(out.type) !== 'image') {
    throw new Error(`“${file.name}” can't be shown here (${out.type.replace('image/', '').toUpperCase() || 'unknown'}): save it as PNG or JPG first.`);
  }
  if (out.blob.size > TICKET_MAX_FILE_BYTES) throw new Error(`“${file.name}” is ${formatFileSize(out.blob.size)}: screenshots can be up to ${formatFileSize(TICKET_MAX_FILE_BYTES)}.`);
  return out;
}

export interface PickedShot { key: number; name: string; preview: string | null; prepared?: PreparedFile; error?: string }

/** the screenshots picked for a ticket, at most `room` of them, each prepared as it's added */
export function useShotPicker(room: number) {
  const [shots, setShots] = useState<PickedShot[]>([]);
  const [note, setNote] = useState('');
  const seq = useRef(0);
  const live = useRef(shots);
  live.current = shots;
  const add = useCallback((files: File[]) => {
    const pics = files.filter(isPicture);
    const free = room - live.current.filter((s) => !s.error).length;
    setNote(pics.length < files.length ? 'Only pictures can go on a ticket.' : pics.length > free ? `A ticket holds up to ${TICKET_MAX_FILES} screenshots.` : '');
    for (const file of pics.slice(0, Math.max(0, free))) {
      const key = ++seq.current;
      // a pasted screenshot is called image.png: give it a name that says what it is
      const name = /^image\.(png|jpe?g|gif|webp)$/i.test(file.name) ? `Screenshot ${key}.${file.name.split('.').pop()}` : file.name;
      setShots((s) => [...s, { key, name, preview: null }]);
      prepareShot(file).then(
        (prepared) => setShots((s) => s.map((x) => (x.key === key ? { ...x, prepared: { ...prepared, name: prepared.name === file.name ? name : prepared.name }, preview: prepared.preview } : x))),
        (err: unknown) => setShots((s) => s.map((x) => (x.key === key ? { ...x, error: (err as Error).message || 'This picture can’t be read.' } : x))),
      );
    }
  }, [room]);
  const remove = (key: number) => setShots((s) => s.filter((x) => x.key !== key));
  const ready = shots.filter((s) => s.prepared);
  const busy = shots.some((s) => !s.prepared && !s.error);
  return { shots, add, remove, ready, busy, note };
}

/** pictures pasted anywhere on the page (Ctrl+V of a screenshot) while `on` */
export function usePastedPictures(on: boolean, onFiles: (files: File[]) => void) {
  const cb = useRef(onFiles);
  cb.current = onFiles;
  useEffect(() => {
    if (!on) return;
    const paste = (e: ClipboardEvent) => {
      const files = [...(e.clipboardData?.files ?? [])].filter(isPicture);
      if (!files.length) return; // text pastes as text
      e.preventDefault();
      cb.current(files);
    };
    document.addEventListener('paste', paste);
    return () => document.removeEventListener('paste', paste);
  }, [on]);
}

/** the box to paste, drop or choose screenshots into, with what's been picked */
export function ShotPicker({ picker, room }: { picker: ReturnType<typeof useShotPicker>; room: number }) {
  const input = useRef<HTMLInputElement>(null);
  const [over, setOver] = useState(false);
  const full = picker.shots.filter((s) => !s.error).length >= room;
  const drop = (e: DragEvent) => { e.preventDefault(); setOver(false); picker.add([...e.dataTransfer.files]); };
  return (
    <div className="tk-shots-pick">
      <div
        className={`tk-drop${over ? ' is-over' : ''}${full ? ' is-full' : ''}`}
        onDragOver={(e) => { e.preventDefault(); if (!over) setOver(true); }}
        onDragLeave={() => setOver(false)}
        onDrop={drop}
      >
        <span>{full ? `${room} screenshots, the most a ticket holds.` : <>Paste a screenshot (<kbd>Ctrl</kbd>+<kbd>V</kbd>), drop pictures here, or</>}</span>
        {!full && <button type="button" className="btn btn-sm" onClick={() => input.current?.click()}>Choose pictures</button>}
        <input
          ref={input}
          type="file"
          accept="image/*"
          multiple
          hidden
          aria-label="Choose screenshots"
          onChange={(e) => { picker.add([...(e.currentTarget.files ?? [])]); e.currentTarget.value = ''; }}
        />
      </div>
      {picker.note && <p className="tk-shots-note" role="status">{picker.note}</p>}
      {picker.shots.length > 0 && (
        <ul className="tk-shots" aria-label="Screenshots to send">
          {picker.shots.map((s) => (
            <li key={s.key} className={`tk-shot${s.error ? ' is-error' : ''}`} title={s.error ?? s.name}>
              {s.preview ? <img src={s.preview} alt={s.name} /> : <span className="tk-shot-ph">{s.error ? '!' : '…'}</span>}
              <button type="button" className="tk-shot-x" aria-label={`Remove ${s.name}`} onClick={() => picker.remove(s.key)}><IconClose size={12} /></button>
            </li>
          ))}
        </ul>
      )}
      {picker.shots.filter((s) => s.error).map((s) => <p key={s.key} className="tk-shots-err">{s.error}</p>)}
    </div>
  );
}

/** a ticket's screenshots as thumbnails that open full size. `editable` (its sender, while it's open) adds
 *  more and takes one off. */
export function TicketShots({ ticket, editable }: { ticket: Ticket; editable?: boolean }) {
  const nameOf = useNameResolver();
  const [open, setOpen] = useState<number | null>(null);
  const remove = useRemoveTicketFile();
  const refresh = useRefreshTickets();
  const input = useRef<HTMLInputElement>(null);
  const [adding, setAdding] = useState(0);
  const [err, setErr] = useState<string | null>(null);
  const room = TICKET_MAX_FILES - ticket.files.length;
  const addMore = async (files: File[]) => {
    setErr(null);
    const pics = files.filter(isPicture).slice(0, room);
    if (!pics.length) { setErr(files.length ? 'Only pictures can go on a ticket.' : null); return; }
    setAdding(pics.length);
    for (const f of pics) {
      try {
        await uploadTicketFile(ticket.id, await prepareShot(f));
      } catch (e) {
        setErr((e as Error).message || 'A screenshot didn’t go on.');
      }
      setAdding((n) => n - 1);
    }
    await refresh();
  };
  if (!ticket.files.length && !editable) return null;
  const photos = ticket.files.map((f) => ({ ...f, from: nameOf(ticket.author) || ticket.author, at: Date.parse(ticket.createdAt) }));
  return (
    <div className="tk-shots-wrap">
      {ticket.files.length > 0 && (
        <ul className="tk-shots" aria-label={`Screenshots on ${ticket.ref}`}>
          {ticket.files.map((f, i) => (
            <li key={f.id} className="tk-shot">
              <button type="button" className="tk-shot-open" aria-label={`Open ${f.name}`} onClick={() => setOpen(i)}>
                <img src={ticketFileUrl(f.id)} alt={f.name} loading="lazy" />
              </button>
              {editable && (
                <button
                  type="button"
                  className="tk-shot-x"
                  aria-label={`Take ${f.name} off`}
                  disabled={remove.isPending && remove.variables === f.id}
                  onClick={() => remove.mutate(f.id)}
                ><IconClose size={12} /></button>
              )}
            </li>
          ))}
        </ul>
      )}
      {editable && room > 0 && (
        <>
          <button type="button" className="btn btn-sm btn-ghost tk-shots-add" disabled={adding > 0} onClick={() => input.current?.click()}>
            {adding > 0 ? `Adding ${adding}…` : ticket.files.length ? 'Add screenshots' : 'Add a screenshot'}
          </button>
          <input
            ref={input}
            type="file"
            accept="image/*"
            multiple
            hidden
            aria-label={`Add screenshots to ${ticket.ref}`}
            onChange={(e) => { void addMore([...(e.currentTarget.files ?? [])]); e.currentTarget.value = ''; }}
          />
        </>
      )}
      {err && <p className="tk-shots-err">{err}</p>}
      {remove.isError && <InlineError error={remove.error} onDismiss={() => remove.reset()} />}
      {open != null && <ChatLightbox photos={photos} index={open} onIndex={setOpen} onClose={() => setOpen(null)} fileUrl={ticketFileUrl} />}
    </div>
  );
}
